// Basic
import { useEffect, useState } from 'react';
// Store & actions
import useSessionStore from '@/app/store/session';
import actionsUsers from '@/app/modules/users/monetic/api/actionsUsers';
import actionsSession from '@/app/modules/session/api/actionsSession';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import DetailsGrid from '@/common-utils/components/renders/detailsGrid/DetailsGrid';
import ModalPermissions from '@/app/modules/users/monetic/components/modals/permissions/ModalPermissions';
import ModalRemoveUser from '@/app/modules/users/monetic/components/modals/ModalRemoveUser';
import ModalResetPassword from '@/app/modules/users/monetic/components/modals/ModalResetPassword';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import ActivateDeactivate from '@/app/modules/users/monetic/components/dropdown/actions/ActivateDeactivate';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/common-utils/components/ui/accordion';
// Common
import RouteUrls from '../../enums/RouteUrls';
import userFields from '@/app/modules/users/monetic/fields/userFields';
import ICONS from '@/common-utils/enums/icons';
import UserPermissions from '@/app/modules/users/monetic/components/permissions/UserPermissions';
import Permissions from '@/app/permissions/Permissions';
import { useParams } from 'react-router-dom';
// Lang
import { nsUser } from '@/app/i18n/namespaces';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';

/**
 * @returns {JSX} UserDetails
 */
function UserDetails() {

    // URL Params
    const { userId } = useParams();

    // Store
    const session = useSessionStore(s => s.session);

    // State
    const [userPermissions, setUserPermissions] = useState(undefined);
    const [showModalResetPassword, setShowModalResetPassword] = useState(false);
    const [showModalUpdatePermissions, setShowModalUpdatePermissions] = useState(false);
    const [showModalRemoveUser, setShowModalRemoveUser] = useState(false);
    const [openUserDetails, setOpenUserDetails] = useState(true);
    const [openUserPermissions, setOpenUserPermissions] = useState(false);

    // API Calls
    const { data: user, isLoading, isError, refetch } = actionsUsers.getUser(userId);

    const submitConnectAs = actionsSession.connectAs(() => { }, () => { });

    useEffect(() => {
        if (!isLoading && !isError && user) {
            setUserPermissions(user[userFields.PERMISSIONS.key]);
        }
    }, [user, isLoading, isError]);


    // Constants
    const userDetailsFields = Object.fromEntries(
        Object.entries(userFields).filter(([key]) =>
            ['EMAIL', 'STATUS', 'CREATED_AT', 'UPDATED_AT', 'LAST_LOGIN_AT', 'PROFILE'].includes(key)
        )
    );
    const loggedUserId = session.uuid;
    const permissions = session.permissions;

    // Functions
    const setLaunchRefetch = () => refetch();

    return (
        <Page>
            <Page.Header route={RouteUrls.USER_DETAILS} routeBase={RouteUrls.USERS} />
            {
                <Page.Body dataCy='page-user-details'>
                    <Accordion type='multiple' defaultValue={['item-1']}>
                        <AccordionItem className='rounded-md' value='item-1' >
                            <AccordionTrigger
                                className='opacity-[.8]'
                                open={openUserDetails}
                                onClick={() => setOpenUserDetails(!openUserDetails)}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.USER} />
                                    <Entity entity={'page.users.accordions.details'} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <>
                                    <PageActions actions={
                                        [
                                            {
                                                icon: ICONS.USER_SETTINGS,
                                                entity: 'actions.row.permissions_update',
                                                namespace: nsUser,
                                                onClick: () => setShowModalUpdatePermissions(true),
                                                visible: true,
                                                disabled:
                                                    !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [Permissions.USER_CREATE.name]
                                                    }) ||
                                                    userId === loggedUserId,
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            {
                                                icon: ICONS.CONTACT_MAIL,
                                                entity: 'actions.row.reset_password',
                                                namespace: nsUser,
                                                onClick: () => setShowModalResetPassword(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [Permissions.USER_RESEND_ACTIVATION_MAIL.name]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            {
                                                icon: ICONS.USER_CONNECT_AS,
                                                entity: 'actions.row.connect_as',
                                                namespace: nsUser,
                                                onClick: () => submitConnectAs.mutate(userId),
                                                visible: true,
                                                disabled:
                                                    !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [Permissions.USER_LOGIN_AS_USER.name]
                                                    }) ||
                                                    userId === loggedUserId,
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            ...ActivateDeactivate(user, permissions, setLaunchRefetch),
                                            {
                                                icon: ICONS.SUBSCRIPTION_TERMINATE,
                                                entity: 'actions.row.remove',
                                                namespace: nsUser,
                                                className: 'text-danger',
                                                onClick: () => setShowModalRemoveUser(true),
                                                visible: true,
                                                variant: 'critical',
                                                disabled:
                                                    !hasRequiredPermissions({
                                                        permissions: permissions,
                                                        requiredPermissions: [Permissions.USER_REMOVE.name]
                                                    }) ||
                                                    userId === loggedUserId,
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }
                                        ]
                                    }
                                        retractable
                                    />
                                    {
                                        isLoading ? <Spinner /> :
                                            <DetailsGrid data={user} fields={userDetailsFields} />
                                    }
                                </>
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem className='rounded-md' value='item-2' >
                            <AccordionTrigger
                                className='opacity-[.8]'
                                open={openUserPermissions}
                                onClick={() => setOpenUserPermissions(!openUserPermissions)}
                                data-cy='accordion-user-permissions'
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.USER_SETTINGS} />
                                    <Entity entity={'page.users.accordions.permissions'} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <>
                                    {
                                        isLoading || !userPermissions ? <Spinner /> :
                                            userPermissions &&
                                            <UserPermissions userPermissions={userPermissions} isUpdate={false} />
                                    }
                                </>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </Page.Body>
            }
            {
                showModalResetPassword &&
                <ModalResetPassword
                    isOpen={showModalResetPassword}
                    setIsOpen={setShowModalResetPassword}
                    userId={userId}
                    userEmail={user[userFields.EMAIL.key]}
                />
            }
            {
                showModalRemoveUser &&
                <ModalRemoveUser
                    isOpen={showModalRemoveUser}
                    setIsOpen={setShowModalRemoveUser}
                    userId={userId}
                    userEmail={user[userFields.EMAIL.key]}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showModalUpdatePermissions &&
                <ModalPermissions
                    isOpen={showModalUpdatePermissions}
                    setIsOpen={setShowModalUpdatePermissions}
                    userId={userId}
                    organizationId={user[userFields.ORGANIZATION_UUID.key]}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
        </Page>
    );
}

export default UserDetails;